import type { InfoScreenDesign, InfoScreenLayout, InfoScreenPage } from "@/types/infoScreen";

export type DesignProposal = {
  id: InfoScreenDesign;
  name: string;
  description: string;
  /** CSS gradient for gallery thumbnail */
  previewGradient: string;
  previewAccent: string;
  layout: InfoScreenLayout;
  tags: string[];
};

export const INFO_SCREEN_DESIGNS: DesignProposal[] = [
  {
    id: "glass",
    name: "Glass",
    description: "Glasflächen, Weichzeichner und schwebende Karten – luftig und modern.",
    previewGradient: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 45%, #a5b4fc 100%)",
    previewAccent: "#4f46e5",
    layout: "split",
    tags: ["modern", "hell"],
  },
  {
    id: "cinematic",
    name: "Cinematic",
    description: "Dunkles Kino-Feeling mit Vignette und großflächigem Bild.",
    previewGradient: "linear-gradient(160deg, #0f0f14 0%, #1a1a2e 50%, #16213e 100%)",
    previewAccent: "#e94560",
    layout: "full_bleed",
    tags: ["dunkel", "bild"],
  },
  {
    id: "editorial",
    name: "Editorial",
    description: "Magazin-Ästhetik mit asymmetrischer Bild-Text-Anordnung.",
    previewGradient: "linear-gradient(120deg, #faf8f5 0%, #f0ebe3 100%)",
    previewAccent: "#1c1917",
    layout: "split",
    tags: ["typografie", "asymmetrisch"],
  },
  {
    id: "neon",
    name: "Neon",
    description: "Leuchtende Akzente auf tiefschwarzem Grund – ideal für Abendbetrieb.",
    previewGradient: "linear-gradient(145deg, #0a0a12 0%, #12121f 60%, #1a0a2e 100%)",
    previewAccent: "#22d3ee",
    layout: "image_focus",
    tags: ["dunkel", "akzent"],
  },
  {
    id: "nature",
    name: "Nature",
    description: "Erdige Grüntöne und organische Formen – perfekt für Camping & Natur.",
    previewGradient: "linear-gradient(150deg, #ecfdf5 0%, #d1fae5 40%, #6ee7b7 100%)",
    previewAccent: "#059669",
    layout: "split",
    tags: ["grün", "outdoor"],
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Reduziert auf Typografie und viel Weißraum – klar und zeitlos.",
    previewGradient: "linear-gradient(180deg, #ffffff 0%, #f4f4f5 100%)",
    previewAccent: "#18181b",
    layout: "centered",
    tags: ["reduziert", "hell"],
  },
  {
    id: "warm",
    name: "Warm",
    description: "Einladende Warmtöne wie in einer Boutique-Lounge.",
    previewGradient: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)",
    previewAccent: "#ea580c",
    layout: "split",
    tags: ["hell", "gemütlich"],
  },
  {
    id: "bold",
    name: "Bold",
    description: "Große, kraftvolle Schrift und starke Kontraste.",
    previewGradient: "linear-gradient(125deg, #fef08a 0%, #facc15 30%, #0f172a 30%, #0f172a 100%)",
    previewAccent: "#facc15",
    layout: "full_bleed",
    tags: ["kontrast", "typografie"],
  },
  {
    id: "aurora",
    name: "Aurora",
    description: "Wechselnde Farbverläufe wie Polarlichter im Hintergrund.",
    previewGradient: "linear-gradient(120deg, #312e81 0%, #7c3aed 35%, #06b6d4 70%, #34d399 100%)",
    previewAccent: "#a78bfa",
    layout: "centered",
    tags: ["farbig", "dynamisch"],
  },
  {
    id: "sunset",
    name: "Sunset",
    description: "Warme Abendstimmung mit Orange-, Rosa- und Violett-Tönen.",
    previewGradient: "linear-gradient(160deg, #fef3c7 0%, #fb923c 35%, #e11d48 70%, #7c3aed 100%)",
    previewAccent: "#f97316",
    layout: "image_focus",
    tags: ["warm", "bild"],
  },
  {
    id: "ocean",
    name: "Ocean",
    description: "Tiefes Blau und Türkis – frisch wie Meer und Himmel.",
    previewGradient: "linear-gradient(155deg, #ecfeff 0%, #67e8f9 40%, #0369a1 100%)",
    previewAccent: "#0891b2",
    layout: "split",
    tags: ["blau", "frisch"],
  },
  {
    id: "organic",
    name: "Organic",
    description: "Weiche Blob-Formen und fließende Überlagerungen statt harter Kanten.",
    previewGradient: "linear-gradient(140deg, #fdf4ff 0%, #fae8ff 40%, #e9d5ff 100%)",
    previewAccent: "#c026d3",
    layout: "full_bleed",
    tags: ["weich", "modern"],
  },
  {
    id: "brutalist",
    name: "Brutalist",
    description: "Rohe Typografie, harte Kanten und bewusst reduzierte Dekoration.",
    previewGradient: "linear-gradient(180deg, #fafafa 0%, #e4e4e7 50%, #18181b 50%, #18181b 100%)",
    previewAccent: "#18181b",
    layout: "centered",
    tags: ["kontrast", "roh"],
  },
  {
    id: "linen",
    name: "Linen",
    description: "Warmes Papier, Serifenschrift und viel Ruhe – wie eine gedruckte Willkommenskarte.",
    previewGradient: "linear-gradient(165deg, #f7f3eb 0%, #e7e0d4 100%)",
    previewAccent: "#8c7355",
    layout: "centered",
    tags: ["hell", "typografie"],
  },
  {
    id: "forest",
    name: "Forest",
    description: "Tiefes Waldgrün mit hellem Moos-Akzent – für Abend und Naturmotive.",
    previewGradient: "linear-gradient(155deg, #052e16 0%, #166534 55%, #14532d 100%)",
    previewAccent: "#d9f99d",
    layout: "image_focus",
    tags: ["dunkel", "outdoor"],
  },
  {
    id: "dune",
    name: "Dune",
    description: "Sand, Stein und weiche Kanten – ruhig wie ein Platz an der Küste.",
    previewGradient: "linear-gradient(140deg, #faf6f1 0%, #e7d3b8 70%, #c4a574 100%)",
    previewAccent: "#a16207",
    layout: "split",
    tags: ["hell", "outdoor"],
  },
  {
    id: "alpine",
    name: "Alpine",
    description: "Klares Grau, Schneeweiß und scharfe Kanten – bergig und sachlich.",
    previewGradient: "linear-gradient(180deg, #f8fafc 0%, #cbd5e1 48%, #0f172a 48%, #0f172a 100%)",
    previewAccent: "#0f172a",
    layout: "split",
    tags: ["hell", "klar"],
  },
  {
    id: "nordic",
    name: "Nordic",
    description: "Helles Birkenholz und gedämpftes Blaugrau – reduziert und nordisch.",
    previewGradient: "linear-gradient(160deg, #f4f7f8 0%, #d5e2e6 100%)",
    previewAccent: "#5b7c8a",
    layout: "centered",
    tags: ["hell", "reduziert"],
  },
  {
    id: "harbor",
    name: "Harbor",
    description: "Marineblau mit Messing – wie ein Hafenhinweis am Abend.",
    previewGradient: "linear-gradient(150deg, #0b1f33 0%, #1a4066 70%, #e4b15a 70%, #e4b15a 100%)",
    previewAccent: "#e4b15a",
    layout: "split",
    tags: ["dunkel", "kontrast"],
  },
  {
    id: "terracotta",
    name: "Terracotta",
    description: "Ton, Creme und ein warmer Akzent – mediterran und einladend.",
    previewGradient: "linear-gradient(135deg, #fbf6f1 0%, #e8cbb8 55%, #c2410c 100%)",
    previewAccent: "#c2410c",
    layout: "split",
    tags: ["warm", "hell"],
  },
  {
    id: "meadow",
    name: "Meadow",
    description: "Frisches Frühlingsgrün und helle Karten – freundlich am Tag.",
    previewGradient: "linear-gradient(150deg, #f7fee7 0%, #d9f99d 60%, #84cc16 100%)",
    previewAccent: "#65a30d",
    layout: "centered",
    tags: ["grün", "hell"],
  },
  {
    id: "frost",
    name: "Frost",
    description: "Eisblau und milchige Flächen – klar wie ein Wintermorgen.",
    previewGradient: "linear-gradient(160deg, #f0f9ff 0%, #bae6fd 55%, #38bdf8 100%)",
    previewAccent: "#0284c7",
    layout: "split",
    tags: ["hell", "frisch"],
  },
  {
    id: "poster",
    name: "Poster",
    description: "Großes Plakat: knallige Farbfläche und wuchtige Schrift über dem Bild.",
    previewGradient: "linear-gradient(160deg, #0a0a0a 0%, #0a0a0a 42%, #facc15 42%, #f43f5e 100%)",
    previewAccent: "#facc15",
    layout: "full_bleed",
    tags: ["kontrast", "typografie"],
  },
  {
    id: "midnight",
    name: "Midnight",
    description: "Nachthimmel in Tintenblau, silberne Schrift und feine Lichtpunkte.",
    previewGradient: "linear-gradient(165deg, #020617 0%, #1e1b4b 70%, #94a3b8 100%)",
    previewAccent: "#e2e8f0",
    layout: "image_focus",
    tags: ["dunkel", "bild"],
  },
  {
    id: "lagoon",
    name: "Lagoon",
    description: "Tiefes Türkis mit hellem Schimmer – frisch und weit.",
    previewGradient: "linear-gradient(155deg, #042f2e 0%, #0f766e 50%, #5eead4 100%)",
    previewAccent: "#5eead4",
    layout: "image_focus",
    tags: ["farbig", "frisch"],
  },
  {
    id: "chalk",
    name: "Chalk",
    description: "Grüne Tafel mit heller Schrift – für Hinweise, Zeiten und Regeln.",
    previewGradient: "linear-gradient(160deg, #14532d 0%, #166534 100%)",
    previewAccent: "#fef08a",
    layout: "centered",
    tags: ["dunkel", "hinweis"],
  },
  {
    id: "campfire",
    name: "Campfire",
    description: "Dunkles Holz und warme Glut – für Abendprogramm und Lagerfeuer.",
    previewGradient: "linear-gradient(180deg, #1c0a00 0%, #7c2d12 62%, #fdba74 100%)",
    previewAccent: "#fdba74",
    layout: "full_bleed",
    tags: ["dunkel", "warm"],
  },
  {
    id: "stone",
    name: "Stone",
    description: "Stein und Schiefer, versetzte Karten und kaum Rundung.",
    previewGradient: "linear-gradient(135deg, #f5f5f4 0%, #a8a29e 55%, #44403c 100%)",
    previewAccent: "#44403c",
    layout: "split",
    tags: ["hell", "ruhig"],
  },
  {
    id: "ribbon",
    name: "Ribbon",
    description: "Ein farbiges Band über dem Bild – Hinweis und Bild in einem Blick.",
    previewGradient: "linear-gradient(115deg, #fafafa 0%, #fafafa 38%, #6d28d9 38%, #6d28d9 62%, #fafafa 62%, #fafafa 100%)",
    previewAccent: "#6d28d9",
    layout: "full_bleed",
    tags: ["kontrast", "hinweis"],
  },
  {
    id: "classic",
    name: "Classic",
    description: "Das bewährte Standard-Design des Infobildschirms.",
    previewGradient: "linear-gradient(165deg, #f8f9fb 0%, #e8ecf2 100%)",
    previewAccent: "#2563eb",
    layout: "split",
    tags: ["standard"],
  },
];

const DESIGN_BY_ID = new Map(INFO_SCREEN_DESIGNS.map((d) => [d.id, d]));

export function getDesignProposal(id: InfoScreenDesign): DesignProposal {
  return DESIGN_BY_ID.get(id) ?? DESIGN_BY_ID.get("classic")!;
}

export function createPageFromDesign(
  designId: InfoScreenDesign,
  name: string,
  sortOrder: number,
): InfoScreenPage {
  const proposal = getDesignProposal(designId);
  const id = crypto.randomUUID();

  const baseText = {
    headline: {
      id: crypto.randomUUID(),
      title: null as string | null,
      body: "Willkommen",
      variant: "headline" as const,
      sort_order: 10,
      column: null as "left" | "right" | null,
    },
    body: {
      id: crypto.randomUUID(),
      title: null as string | null,
      body: "Passen Sie diesen Text und Ihre Bilder an – das Design legt Farben, Typografie und Anordnung fest.",
      variant: "body" as const,
      sort_order: 20,
      column: null as "left" | "right" | null,
    },
    highlight: {
      id: crypto.randomUUID(),
      title: "Hinweis",
      body: "Öffnungszeiten, WLAN oder wichtige Meldungen hier eintragen.",
      variant: "highlight" as const,
      sort_order: 30,
      column: null as "left" | "right" | null,
    },
  };

  const isImmersive =
    proposal.layout === "full_bleed" || proposal.layout === "image_focus";

  return {
    id,
    name,
    enabled: true,
    sort_order: sortOrder,
    layout: proposal.layout,
    design: designId,
    slideshow_interval_seconds: 8,
    images: [],
    text_blocks: isImmersive ? [] : [baseText.headline, baseText.body, baseText.highlight],
    column_left_blocks: [],
    column_right_blocks: [],
  };
}
